import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import styled from 'styled-components';
import { Icon } from '../common/Icon';
import { 
  FaBold, 
  FaItalic, 
  FaListUl, 
  FaListOl, 
  FaLink, 
  FaImage,
  FaUndo,
  FaRedo
} from 'react-icons/fa';

const EditorContainer = styled.div`
  border: 1px solid #444;
  border-radius: 8px;
  background-color: rgb(31, 31, 31);
  overflow: hidden;
`;

const Toolbar = styled.div`
  display: flex;
  gap: 0.25rem;
  padding: 0.75rem;
  border-bottom: 1px solid #444;
  flex-wrap: wrap;
  background-color: rgb(25, 25, 25);
`;

const ToolbarGroup = styled.div`
  display: flex;
  gap: 0.25rem;
  padding-right: 0.75rem;
  border-right: 1px solid #444;

  &:last-child {
    border-right: none;
    padding-right: 0;
  }
`;

const ToolbarButton = styled.button<{ $active?: boolean }>`
  padding: 0.5rem;
  background-color: ${props => props.$active ? '#007bff' : 'transparent'};
  color: ${props => props.$active ? 'white' : '#ccc'};
  border: 1px solid ${props => props.$active ? '#007bff' : '#555'};
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  width: 36px;
  height: 36px;

  &:hover {
    background-color: ${props => props.$active ? '#0056b3' : '#333'};
    border-color: ${props => props.$active ? '#0056b3' : '#666'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

`;

const HeadingButton = styled.button<{ $active?: boolean }>`
  padding: 0.5rem 0.75rem;
  background-color: ${props => props.$active ? '#007bff' : 'transparent'};
  color: ${props => props.$active ? 'white' : '#ccc'};
  border: 1px solid ${props => props.$active ? '#007bff' : '#555'};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 600;
  height: 36px;

  &:hover {
    background-color: ${props => props.$active ? '#0056b3' : '#333'};
    border-color: ${props => props.$active ? '#0056b3' : '#666'};
  }
`;

const EditorContentWrapper = styled.div`
  .ProseMirror {
    padding: 1.5rem;
    min-height: 400px;
    color: #ddd;
    font-size: 1rem;
    line-height: 1.7;

    &:focus {
      outline: none;
    }

    h1 {
      color: white;
      font-size: 2rem;
      margin: 1.5rem 0 1rem 0;
      line-height: 1.3;
    }

    h2 {
      color: white;
      font-size: 1.5rem;
      margin: 1.25rem 0 0.75rem 0;
      line-height: 1.3;
    }

    h3 {
      color: white;
      font-size: 1.25rem;
      margin: 1rem 0 0.5rem 0;
      line-height: 1.3;
    }

    p {
      margin: 0.75rem 0;
    }

    a {
      color: #007bff;
      text-decoration: underline;
      cursor: pointer;

      &:hover {
        color: #0056b3;
      }
    }

    ul, ol {
      padding-left: 2rem;
      margin: 0.75rem 0;
    }

    li {
      margin: 0.25rem 0;
    }

    img {
      max-width: 100%;
      height: auto;
      border-radius: 8px;
      margin: 1rem 0;
    }

    blockquote {
      border-left: 4px solid #007bff;
      padding-left: 1rem;
      margin: 1rem 0;
      color: #aaa;
    }

    code {
      background-color: rgb(45, 45, 45);
      padding: 0.2rem 0.4rem;
      border-radius: 4px;
      font-family: monospace;
    }

    pre {
      background-color: rgb(45, 45, 45);
      padding: 1rem;
      border-radius: 8px;
      overflow-x: auto;
      margin: 1rem 0;

      code {
        background: none;
        padding: 0;
      }
    }
  }
`;

interface BlogWYSIWYGProps {
  content: string;
  onChange: (html: string) => void;
}

const BlogWYSIWYG: React.FC<BlogWYSIWYGProps> = ({ content, onChange }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'blog-image',
        },
      }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  if (!editor) {
    return null;
  }

  const addLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const addImage = () => {
    const url = window.prompt('Enter image URL:');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  return (
    <EditorContainer>
      <Toolbar>
        <ToolbarGroup>
          <HeadingButton
            $active={editor.isActive('heading', { level: 1 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            type="button"
          >
            H1
          </HeadingButton>
          <HeadingButton
            $active={editor.isActive('heading', { level: 2 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            type="button"
          >
            H2
          </HeadingButton>
          <HeadingButton
            $active={editor.isActive('heading', { level: 3 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            type="button"
          >
            H3
          </HeadingButton>
        </ToolbarGroup>

        <ToolbarGroup>
          <ToolbarButton
            $active={editor.isActive('bold')}
            onClick={() => editor.chain().focus().toggleBold().run()}
            type="button"
            title="Bold"
          >
            <Icon icon={FaBold} size={16} />
          </ToolbarButton>
          <ToolbarButton
            $active={editor.isActive('italic')}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            type="button"
            title="Italic"
          >
            <Icon icon={FaItalic} size={16} />
          </ToolbarButton>
        </ToolbarGroup>

        <ToolbarGroup>
          <ToolbarButton
            $active={editor.isActive('bulletList')}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            type="button"
            title="Bullet List"
          >
            <Icon icon={FaListUl} size={16} />
          </ToolbarButton>
          <ToolbarButton
            $active={editor.isActive('orderedList')}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            type="button"
            title="Ordered List"
          >
            <Icon icon={FaListOl} size={16} />
          </ToolbarButton>
        </ToolbarGroup>

        <ToolbarGroup>
          <ToolbarButton
            $active={editor.isActive('link')}
            onClick={addLink}
            type="button"
            title="Add Link"
          >
            <Icon icon={FaLink} size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={addImage}
            type="button"
            title="Add Image"
          >
            <Icon icon={FaImage} size={16} />
          </ToolbarButton>
        </ToolbarGroup>

        <ToolbarGroup>
          <ToolbarButton
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            type="button"
            title="Undo"
          >
            <Icon icon={FaUndo} size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            type="button"
            title="Redo"
          >
            <Icon icon={FaRedo} size={16} />
          </ToolbarButton>
        </ToolbarGroup>
      </Toolbar>

      <EditorContentWrapper>
        <EditorContent editor={editor} />
      </EditorContentWrapper>
    </EditorContainer>
  );
};

export default BlogWYSIWYG;
